import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import {
  Briefcase,
  Circle,
  Clock,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useGetOnlineEmployees } from '@/pages/Employee/api/employeeStatus.api';
import { useUserStatusStore } from '@/store/useUserStatusStore';
import { getAvatarThumbUrl } from '@/utils/avatarHelpers';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';

import { UserStatusBadge } from './UserStatusBadge';

// ==================== Types ====================
export interface OnlineUsersListProps {
  className?: string;
  maxHeight?: string;
  showSearch?: boolean;
  onUserClick?: (userId: string) => void;
  showOnlyOnline?: boolean; // NEW: Whether to show only online users or all users
}

// ==================== Helper Functions ====================
/**
 * Calculate if user is online based on lastSeen (within last minute)
 */
const calculateIsOnline = (lastSeen?: string): boolean => {
  if (!lastSeen) return false;

  try {
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffInSeconds = (now.getTime() - lastSeenDate.getTime()) / 1000;
    return diffInSeconds < 60; // Online if within last 60 seconds
  } catch (error) {
    return false;
  }
};

/**
 * Format last seen timestamp in Arabic with "since" format
 * Always shows "منذ X" regardless of online status
 */
const formatLastSeenArabic = (lastSeen?: string): string => {
  if (!lastSeen) {
    return 'غير متصل';
  }

  try {
    const date = new Date(lastSeen);
    const distance = formatDistanceToNow(date, { locale: ar });
    return `منذ ${distance}`;
  } catch {
    return 'غير متصل';
  }
};

// ==================== Component ====================
export const OnlineUsersList = React.memo(function OnlineUsersList({
  className,
  showSearch = true,
  onUserClick,
  showOnlyOnline = true, // Default to showing only online users
}: OnlineUsersListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch online users from API
  const { data, isLoading, error, refetch, isRefetching } =
    useGetOnlineEmployees();

  // Get the statuses Map from Zustand store (real-time updates)
  // We select the Map itself to maintain a stable subscription
  const statusesMap = useUserStatusStore((state) => state.statuses);

  // Get online count and setMultipleStatuses for updating the store
  const onlineCount = useUserStatusStore((state) => state.getOnlineCount());
  const setMultipleStatuses = useUserStatusStore(
    (state) => state.setMultipleStatuses,
  );

  // Convert Map to array and filter based on showOnlyOnline prop
  // Calculate isOnline based on lastSeen timestamp
  // This memo only recalculates when the Map reference changes (when store updates)
  const storeUsers = useMemo(() => {
    const allStatuses = Array.from(statusesMap.values());

    // Calculate isOnline for each user based on lastSeen
    const usersWithCalculatedStatus = allStatuses.map((user) => ({
      ...user,
      isOnline: calculateIsOnline(user.lastSeen),
    }));

    const users = showOnlyOnline
      ? usersWithCalculatedStatus.filter((status) => status.isOnline)
      : usersWithCalculatedStatus;

    // Log real-time updates for debugging

    return users;
  }, [statusesMap, showOnlyOnline]);

  // Merge API data with real-time store data
  // Calculate isOnline based on lastSeen for both sources
  const mergedUsers = useMemo(() => {
    if (!data?.data) return storeUsers;

    const storeUserIds = new Set(storeUsers.map((u) => u.userId));
    const apiUsers = data.data.map((emp) => {
      const lastSeen = new Date(emp.lastSeen).toISOString();
      return {
        userId: emp._id,
        isOnline: calculateIsOnline(lastSeen), // Calculate based on lastSeen
        lastSeen,
        empName: emp.empName,
        avatar: emp.avatar,
        jobName: emp.job?.jobName,
      };
    });

    // Combine both sources, prioritizing store data for real-time updates
    const combined = [...storeUsers];
    apiUsers.forEach((apiUser) => {
      if (!storeUserIds.has(apiUser.userId)) {
        combined.push(apiUser);
      }
    });

    // Filter by online status if showOnlyOnline is true
    return showOnlyOnline ? combined.filter((user) => user.isOnline) : combined;
  }, [data?.data, storeUsers, showOnlyOnline]);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return mergedUsers;

    const query = searchQuery.toLowerCase();
    return mergedUsers.filter((user) =>
      user.empName?.toLowerCase().includes(query),
    );
  }, [mergedUsers, searchQuery]);

  // Handle refetch with toast notifications
  const handleRefetch = async () => {
    try {
      const result = await refetch();

      if (result.isError || !result.data?.success) {
        toast.error('فشل التحديث', {
          description:
            'لم نتمكن من تحديث قائمة المستخدمين. يرجى المحاولة مرة أخرى.',
          duration: 3000,
        });
      } else {
        // Update Zustand store with fresh data from API
        const freshUsers = result.data.data.map((emp) => ({
          userId: emp._id,
          isOnline: calculateIsOnline(new Date(emp.lastSeen).toISOString()),
          lastSeen: new Date(emp.lastSeen).toISOString(),
          empName: emp.empName,
          avatar: emp.avatar,
          jobName: emp.job?.jobName,
        }));

        setMultipleStatuses(freshUsers);

        const onlineUsersCount = freshUsers.filter((u) => u.isOnline).length;

        toast.success('تم التحديث بنجاح', {
          description: `تم تحديث قائمة المستخدمين. ${onlineUsersCount} ${onlineUsersCount === 1 ? 'مستخدم متصل' : onlineUsersCount === 2 ? 'مستخدمان متصلان' : 'مستخدمين متصلين'}`,
          duration: 3000,
        });
      }
    } catch (error) {
      toast.error('خطأ في التحديث', {
        description: 'حدث خطأ أثناء تحديث القائمة',
        duration: 3000,
      });
    }
  };

  // Loading state with Arabic
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-primary" />
            <span>جاري التحميل...</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <Skeleton className="h-14 w-14 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state with Arabic
  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-destructive" />
            <span>المستخدمون المتصلون</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Circle className="mb-3 h-12 w-12 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">
              فشل تحميل المستخدمين
            </p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              يرجى المحاولة مرة أخرى
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state with Arabic
  if (mergedUsers.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-muted-foreground" />
            <span>المستخدمون</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Users className="mb-3 h-16 w-16 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">
              لا يوجد مستخدمون متصلون
            </p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              سيظهر المستخدمون هنا عند اتصالهم
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Users className="h-6 w-6 text-primary" />
              {onlineCount > 0 && (
                <Circle className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse fill-green-500 text-green-500" />
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold">
                {showOnlyOnline ? 'المتصلون الآن' : 'جميع المستخدمين'}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {mergedUsers.length}{' '}
                {mergedUsers.length === 1
                  ? 'مستخدم'
                  : mergedUsers.length === 2
                    ? 'مستخدمان'
                    : 'مستخدمين'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onlineCount > 0 && (
              <Badge
                variant="secondary"
                className="border-green-500/20 bg-green-500/10 px-3 py-1 text-green-700 dark:text-green-400"
              >
                {onlineCount} نشط
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefetch}
              disabled={isRefetching}
              className="h-8 w-8 rounded-lg transition-all hover:bg-primary/10"
              title="تحديث القائمة"
            >
              <RefreshCw
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-colors hover:text-primary',
                  isRefetching && 'animate-spin',
                )}
              />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <Separator className="mb-4" />
      <CardContent className="space-y-4 pt-0">
        {/* Enhanced Search */}
        {showSearch && (
          <div className="group relative">
            <Search className="absolute right-3 top-3 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
            <Input
              placeholder="ابحث عن مستخدم..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11 border-muted-foreground/20 bg-muted/50 pl-4 pr-10 transition-all focus:bg-background"
            />
          </div>
        )}

        {/* Enhanced Users List */}
        <ScrollArea className="h-[450px]">
          <div className="space-y-1.5 pr-4">
            {filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Search className="mb-3 h-12 w-12 text-muted-foreground/30" />
                <p className="text-sm font-medium text-muted-foreground">
                  لم يتم العثور على نتائج
                </p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  جرب البحث بكلمة أخرى
                </p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.userId}
                  className={cn(
                    'group flex items-center gap-3 rounded-xl p-3 transition-all duration-200',
                    'hover:bg-accent/50 hover:shadow-sm',
                    user.isOnline &&
                      'bg-green-50/50 hover:bg-green-100/50 dark:bg-green-950/10 dark:hover:bg-green-950/20',
                    onUserClick && 'cursor-pointer active:scale-[0.98]',
                  )}
                  onClick={() => onUserClick?.(user.userId)}
                >
                  {/* Enhanced Avatar with status badge */}
                  <div className="relative flex-shrink-0">
                    <Avatar
                      className={cn(
                        'h-14 w-14 border-2 transition-all duration-200 group-hover:scale-105',
                        user.isOnline
                          ? 'border-green-500/30 ring-2 ring-green-500/10'
                          : 'border-border',
                      )}
                    >
                      <AvatarImage
                        src={getAvatarThumbUrl(user.avatar)}
                        alt={user.empName || 'مستخدم'}
                        className="object-cover"
                      />
                      <AvatarFallback
                        className={cn(
                          'text-base font-bold',
                          user.isOnline
                            ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {user.empName?.charAt(0).toUpperCase() || 'م'}
                      </AvatarFallback>
                    </Avatar>
                    <UserStatusBadge
                      userId={user.userId}
                      size="md"
                      position="bottom-right"
                      showTooltip={false}
                    />
                  </div>

                  {/* Enhanced User info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-bold text-foreground transition-colors group-hover:text-primary">
                        {user.empName || 'مستخدم غير معروف'}
                      </p>
                      {/* Always show status badge */}
                      <Badge
                        variant="secondary"
                        className={cn(
                          'px-1.5 py-0 text-[10px]',
                          user.isOnline
                            ? 'border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400'
                            : 'border-gray-500/20 bg-gray-500/10 text-gray-700 dark:text-gray-400',
                        )}
                      >
                        {user.isOnline ? 'نشط' : 'غير نشط'}
                      </Badge>
                    </div>
                    {user.jobName && (
                      <div className="mt-1 flex items-center gap-1.5">
                        <Briefcase className="h-3 w-3 text-primary/60" />
                        <p className="truncate text-[11px] font-medium text-primary/80">
                          {user.jobName}
                        </p>
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <p className="truncate text-xs text-muted-foreground">
                        {formatLastSeenArabic(user.lastSeen)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
});
